import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { ApiError, api } from "../lib/api";

/**
 * Every connector this account has authorized, across spaces (ADR 000043).
 *
 * Read and revoke only, the same shape as agent keys: one place to see what is
 * connected and stop it is what somebody wants when a laptop goes missing, and
 * an authorization outlives the tab it was approved in, so it needs the same
 * page a key already has.
 */
export function ConnectorsRoute() {
  const queryClient = useQueryClient();
  const [failure, setFailure] = useState<string | null>(null);

  const connectors = useQuery({ queryKey: ["connectors"], queryFn: () => api.listConnectors() });
  const spaces = useQuery({ queryKey: ["spaces"], queryFn: () => api.listSpaces() });

  const revoke = useMutation({
    mutationFn: (id: string) => api.revokeConnector(id as never),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["connectors"] });
    },
    onError: (error) => setFailure(describe(error)),
  });

  // A grant names the space it reaches, and its holder is a member of that
  // space, so this lookup is complete. Falling back to the id keeps a
  // connector visible and revokable even if the space listing has not arrived.
  const spaceName = (id: string) => spaces.data?.find((space) => space.id === id)?.name ?? id;

  return (
    <section>
      <header className="page-head">
        <h1>Connectors</h1>
        <p className="muted">
          Clients you have signed in with, such as Claude or another MCP-speaking assistant. Each
          one reaches a single space and nothing else. Revoking a connector stops it on its next
          call, whatever it was in the middle of.
        </p>
      </header>

      {failure && (
        <p role="alert" className="error">
          {failure}
        </p>
      )}

      {connectors.isPending ? (
        <p role="status">Loading…</p>
      ) : connectors.error ? (
        <p className="error">{describe(connectors.error)}</p>
      ) : connectors.data.length === 0 ? (
        <p className="muted">
          Nothing connected yet. Signing in from an MCP client's own setup adds one here.
        </p>
      ) : (
        <ul className="keys">
          {connectors.data.map((grant) => (
            <li key={grant.id} className={grant.revokedAt ? "is-revoked" : ""}>
              <span className="key-body">
                <strong>{grant.clientName}</strong>
                <span className="key-meta">
                  <Link to={`/spaces/${grant.spaceId}`}>{spaceName(grant.spaceId)}</Link>
                  {" · "}
                  connected {shortDate(grant.createdAt)}
                  {grant.lastUsedAt
                    ? ` · last used ${shortDate(grant.lastUsedAt)}`
                    : " · never used"}
                  {grant.revokedAt && " · revoked"}
                </span>
              </span>
              {!grant.revokedAt && (
                <button
                  type="button"
                  className="ghost danger"
                  disabled={revoke.isPending}
                  onClick={() => {
                    // A revoked connector cannot be un-revoked, and whatever
                    // was using it stops working immediately.
                    if (
                      window.confirm(
                        `Revoke "${grant.clientName}"? Anything using it stops working.`,
                      )
                    ) {
                      revoke.mutate(grant.id);
                    }
                  }}
                >
                  Revoke
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function describe(error: unknown): string {
  if (error instanceof ApiError) return error.userMessage;
  return error instanceof Error ? error.message : "Something went wrong.";
}
